import type { World } from './world.js';
import type { EngineConfig } from '../types.js';

/** Ticks of history delay between each chain link. */
const SNAKE_DELAY_TICKS = 10;
/** Maximum history buffer size. */
const SNAKE_MAX_HISTORY = 120;

/**
 * Update enemy AI per archetype.
 * `straight` — moves down at speed.
 * `sine`     — adds sin(timeMs * freq) * amp to X.
 * `aimed`    — moves slowly; fires bullets toward player.
 * `swarm`    — moves down with a slight formation wobble.
 * `snake`    — chain formation: leader sweeps sinusoidal path with
 *              backward movement; followers trail via position history.
 */
export function updateEnemyAi(world: World, dtMs: number, config: EngineConfig): void {
  const dt = dtMs / 1000;
  // Pick first alive player for aimed shots.
  let player = null as null | { pos: { x: number; y: number } };
  for (const p of world.players) {
    if (p.alive && p.respawnMs === 0) {
      player = p;
      break;
    }
  }

  for (const e of world.enemies) {
    if (!e.alive) continue;
    const ageSec = (world.timeMs - e.spawnTimeMs) / 1000;

    switch (e.archetype) {
      case 'straight': {
        // vel.y set at spawn time; just integrate.
        e.pos.x += e.vel.x * dt;
        e.pos.y += e.vel.y * dt;
        break;
      }
      case 'sine': {
        // base downward speed already on vel.y (set at spawn).
        const amp = e.amplitude;
        const freq = e.frequency;
        e.pos.x = e.spawnX + Math.sin(ageSec * freq * Math.PI * 2) * amp;
        e.pos.y += e.vel.y * dt;
        break;
      }
      case 'aimed': {
        e.pos.x += e.vel.x * dt;
        e.pos.y += e.vel.y * dt;
        e.fireCooldownMs = Math.max(0, e.fireCooldownMs - dtMs);
        if (player && e.fireCooldownMs === 0 && e.fireRateHz > 0) {
          const dx = player.pos.x - e.pos.x;
          const dy = player.pos.y - e.pos.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len > 0) {
            const nx = dx / len;
            const ny = dy / len;
            world.spawnBullet({
              pos: { x: e.pos.x, y: e.pos.y + e.radius },
              vel: { x: nx * e.bulletSpeed, y: ny * e.bulletSpeed },
              team: 'enemy',
              kind: 'enemy-aimed',
              damage: 1,
              radius: 4,
            });
          }
          e.fireCooldownMs = 1000 / e.fireRateHz;
        }
        break;
      }
      case 'swarm': {
        // swarm-level horizontal drift based on index (gentle sway).
        const sway = Math.sin(ageSec * 3 + e.swarmIndex * 0.7) * 20;
        e.pos.x = e.spawnX + sway;
        e.pos.y += e.vel.y * dt;
        break;
      }
      case 'orbit': {
        const age = world.timeMs - e.spawnTimeMs;
        const ORBIT_DURATION_MS = 4500;
        const STAGGER_MS = 450;
        const diveStartMs = ORBIT_DURATION_MS + e.swarmIndex * STAGGER_MS;
        const isDiving = age >= diveStartMs;

        if (!isDiving) {
          // Orbit phase: circle formation descending slowly.
          // amplitude = orbit radius, frequency = orbit speed (rad/s).
          const orbitRadius = e.amplitude > 0 ? e.amplitude : 65;
          const orbitSpeed = e.frequency > 0 ? e.frequency : 1.8;
          e.pathPhase += dt * orbitSpeed;
          const angle = e.pathPhase + e.swarmIndex * (Math.PI / 3);
          // Center descends from off-screen top.
          const centerX = e.spawnX;
          const centerY = -e.radius + (age / 1000) * 28;
          e.pos.x = centerX + Math.cos(angle) * orbitRadius;
          e.pos.y = centerY + Math.sin(angle) * orbitRadius;

          // Zero velocity so dive transition has clean start.
          e.vel.x = 0;
          e.vel.y = 0;
        } else {
          // Dive phase: accelerate toward player.
          const MAX_SPEED = 380;
          const ACCEL = 300; // px/s²
          if (player) {
            const dx = player.pos.x - e.pos.x;
            const dy = player.pos.y - e.pos.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            if (len > 0) {
              e.vel.x += (dx / len) * ACCEL * dt;
              e.vel.y += (dy / len) * ACCEL * dt;
            }
          } else {
            // No player — just fall down.
            e.vel.y += 200 * dt;
          }
          // Cap speed.
          const speed = Math.sqrt(e.vel.x * e.vel.x + e.vel.y * e.vel.y);
          if (speed > MAX_SPEED) {
            e.vel.x = (e.vel.x / speed) * MAX_SPEED;
            e.vel.y = (e.vel.y / speed) * MAX_SPEED;
          }
          e.pos.x += e.vel.x * dt;
          e.pos.y += e.vel.y * dt;
        }
        break;
      }
      case 'snake': {
        if (e.swarmIndex === 0) {
          // --- Leader: follow sweeping sinusoidal path ---
          // Accumulate path phase (1.5 rad/s → ~4.2s per full sweep cycle).
          e.pathPhase += dt * 1.5;

          // X: wide sweep across screen.
          const targetX =
            config.worldWidth * 0.5 + Math.sin(e.pathPhase) * config.worldWidth * 0.40;

          // Y: base downward drift + oscillation that causes brief upward movement.
          // At amp 65 > base 40, the snake momentarily reverses — Zybex style.
          const vertBase = 40; // px/s base downward
          const vertAmp = 65;  // px/s oscillation amplitude
          const dy = (vertBase + Math.cos(e.pathPhase * 0.7) * vertAmp) * dt;

          e.pos.x = targetX;
          e.pos.y += dy;

          // Push position to history for this formation.
          let hist = world.formationHistory.get(e.group);
          if (!hist) {
            hist = [];
            world.formationHistory.set(e.group, hist);
          }
          hist.unshift({ x: e.pos.x, y: e.pos.y });
          if (hist.length > SNAKE_MAX_HISTORY) hist.pop();
        } else {
          // --- Follower: read delayed position from history ---
          const hist = world.formationHistory.get(e.group);
          const idx = Math.min(e.swarmIndex * SNAKE_DELAY_TICKS, hist ? hist.length - 1 : 0);
          if (hist && hist.length > 0) {
            const target = hist[idx]!;
            e.pos.x = target.x;
            e.pos.y = target.y;
          }
          // If no history yet (first few frames), stay at spawn position.
        }
        break;
      }
    }

    // Cull enemies that left the bottom of the world.
    if (e.pos.y - e.radius > config.worldHeight + 40) {
      e.alive = false;
    }
  }
}
